import { ComponentVerticalPlacementType } from "building-types/components/componentInstanceTypes";
import type { ComponentTransformVerticalPlacement } from "building-types/components/componentTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { RelatedStairSystemsMap } from "building-types/stairs/relatedStairSystemsMapTypes";
import type {
  GeneratedStairBlock,
  GeneratedStairSystem,
  StairBlock,
  StairSurfacePoly,
} from "building-types/stairs/stairTypes";
import type { RWTable, Table } from "common/data/table";
import { instanceIdForComponent } from "doc/entity/componentInstanceEntityResolve";
import { evaluateEntityForComponentInstance } from "doc/formula/evalFormulas";
import { faceCopy } from "geom/brep";
import type { Mat3 } from "geom/mat3";
import { mat3ToMat4, mat4Mult, mat4Translate, type Mat4 } from "geom/mat4";
import { planeCopy } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { transformFace, transformGeomTagGroup, transformPlane } from "geom/transform";
import {
  applyTransformToOutline,
  applyTransformToOutlineAndEdgeIds,
  componentInstanceMat3ForMirroredTemplate,
} from "../../utils/componentTransforms";
import { componentInstanceLevelId } from "./componentInstanceLevel";

/**
 * Transforms stair plan polygons by applying a Mat3 transform to their outlines and PDF outlines
 * and applying verticalOffset their heights.
 */
export function transformStairPlanPolys(
  stairPlanPolys: (StairSurfacePoly | undefined)[],
  transform: Mat3,
  verticalOffset: number,
): (StairSurfacePoly | undefined)[] {
  return stairPlanPolys.map((poly) => {
    if (!poly) {
      return undefined;
    }
    const transformedOutlinesPDF = poly.outlinesPDF.map((outline) => applyTransformToOutline(transform, outline));
    return {
      ...poly,
      outline: applyTransformToOutline(transform, poly.outline),
      outlinesPDF: transformedOutlinesPDF,
      height: poly.height + verticalOffset,
    };
  });
}

/**
 * Transforms the planes of a stair block.
 */
export function transformStairBlockPlanes(
  planes: GeneratedStairBlock["planes"],
  transformMat4: Mat4,
): GeneratedStairBlock["planes"] {
  const out: GeneratedStairBlock["planes"] = {};
  for (const key of Object.keys(planes) as Array<keyof GeneratedStairBlock["planes"]>) {
    const plane: Plane | undefined = planes[key];
    if (plane) {
      const copy = planeCopy(plane);
      transformPlane(copy, transformMat4);
      out[key] = copy;
    }
  }
  return out;
}

/**
 * Calculates the vertical offset for a stair system based on the component transform's vertical placement.
 * For REPLICATION, uses the offset directly. For REMAPPING, calculates the elevation difference between
 * the source and target levels.
 */
function getStairSystemVerticalOffset(
  sourceLevelId: string,
  verticalPlacement: ComponentTransformVerticalPlacement,
  levels: Table<Level>,
): number {
  const { type } = verticalPlacement;
  if (type === ComponentVerticalPlacementType.REPLICATION) {
    return verticalPlacement.offset;
  }

  const targetLevelId = verticalPlacement.levelRemapping[sourceLevelId];
  if (!targetLevelId) {
    return 0;
  }

  const sourceLevel = levels[sourceLevelId];
  const targetLevel = levels[targetLevelId];
  if (!sourceLevel || !targetLevel) {
    return 0;
  }

  return targetLevel.floorElevation.valueOf() - sourceLevel.floorElevation.valueOf();
}

/**
 * Loop through the stair system's placements (from relatedStairSystemsMap) and add a generated stair system for each instance.
 * Transforms the stair system's blocks, geom, and stair plan polygons.
 */
export function addStairSystemComponentInstances(
  id: string,
  generated: GeneratedStairSystem,
  resultStairSystems: RWTable<GeneratedStairSystem>,
  levels: Table<Level>,
  mirror: boolean,
  mirrorTranslationX: number,
  rawStairBlocks: Table<StairBlock>,
  definitions: OptionDefinitions,
  rawOptionChoices: OptionChoices,
  relatedStairSystemsMap: RelatedStairSystemsMap,
): void {
  for (const transform of relatedStairSystemsMap[id] ?? []) {
    const componentInstanceChain = [...transform.componentInstanceChain];
    const transformedBlocks: RWTable<GeneratedStairBlock> = {};

    const outlineMat = componentInstanceMat3ForMirroredTemplate(mirror, mirrorTranslationX, transform.matrix);
    const verticalOffset = getStairSystemVerticalOffset(generated.levelId, transform.verticalPlacement, levels);
    const transformMat4 = mat4Mult(mat3ToMat4(outlineMat), mat4Translate(0, 0, verticalOffset));

    for (const blockId in generated.blocks) {
      const block = generated.blocks[blockId];
      const rawBlockId = block.stairBlockId ?? blockId;
      const rawBlock = rawStairBlocks[rawBlockId];
      if (rawBlock) {
        const evaluatedBlock = evaluateEntityForComponentInstance(
          rawBlock,
          componentInstanceChain,
          definitions,
          rawOptionChoices,
        );
        if (evaluatedBlock.disabled) {
          continue;
        }
      }

      const instanceBlockId = instanceIdForComponent(blockId, componentInstanceChain);

      // Transform the block's outline and edgeIds.
      const { transformedOutline, transformedEdgeIds } = applyTransformToOutlineAndEdgeIds(
        { ...transform, matrix: outlineMat },
        block.outline,
        block.edgeIds,
      );

      // Transform the block's geom.
      const transformedGeom = transformGeomTagGroup(block.geom, transformMat4);
      const transformedPlanes = transformStairBlockPlanes(block.planes, transformMat4);

      // Transform the block's stairPlanPolys.
      const transformedStairPlanPolys = transformStairPlanPolys(block.stairPlanPolys, outlineMat, verticalOffset);

      // Transform per-block wall trim faces.
      const transformedWallTrimFaces = block.wallTrimFaces.map((face) => transformFace(faceCopy(face), transformMat4));

      transformedBlocks[instanceBlockId] = {
        ...block,
        id: instanceBlockId,
        stairBlockId: instanceBlockId,
        pickHandle: { type: "COMPONENT_INSTANCE", id: transform.instanceId },
        outline: transformedOutline,
        edgeIds: transformedEdgeIds,
        geom: transformedGeom,
        planes: transformedPlanes,
        stairPlanPolys: transformedStairPlanPolys,
        wallTrimFaces: transformedWallTrimFaces,
      };
    }

    const levelId = componentInstanceLevelId(
      generated.levelId,
      levels,
      transform.verticalPlacement,
      transform.componentInstanceChain,
    );

    const instanceStairSystemId = instanceIdForComponent(id, transform.componentInstanceChain);
    resultStairSystems[instanceStairSystemId] = {
      ...generated,
      id: instanceStairSystemId,
      instanceId: transform.instanceId,
      pickHandle: { type: "COMPONENT_INSTANCE", id: transform.instanceId },
      blocks: transformedBlocks,
      levelId,
      wallTrimFaces: [], // Aggregated from blocks after instancing
    };
  }
}
